/**
 * ZONIY - Admin Commissions Page
 * @module AdminCommissions
 * @author Osman - Frontend Developer
 * @description Commission settings management
 * @version 1.0.0
 * Git: Osman <osman@linesoft.com> - Commission settings
 */

import React, { useState, useEffect } from 'react';
import { DollarSign, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCommissions = () => {
  const [settings, setSettings] = useState({
    default_rate: 5.0,
    category_rates: {}
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const categories = ['Engine', 'Transmission', 'Brakes', 'Suspension', 'Electrical', 'Body Parts', 'Interior', 'Exhaust', 'Cooling'];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/commissions`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load commission settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/commissions`, settings);
      toast.success('Commission settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateCategoryRate = (category, rate) => {
    setSettings({
      ...settings,
      category_rates: {
        ...settings.category_rates,
        [category]: parseFloat(rate) || 0
      }
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-slate-200 dark:bg-slate-800 w-64" />
            <div className="h-64 bg-slate-200 dark:bg-slate-800" />
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8" data-testid="admin-commissions">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading font-black text-3xl text-slate-900 dark:text-white uppercase tracking-tight">
            Commission Settings
          </h1>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="btn-industrial"
            data-testid="save-commissions"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                Save Settings
              </span>
            )}
          </Button>
        </div>

        {/* Default Rate */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white uppercase">
                Default Commission Rate
              </h3>
              <p className="text-sm text-slate-500">Applied to all sales unless overridden by category</p>
            </div>
          </div>
          <div className="flex items-center gap-2 max-w-xs">
            <Input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={settings.default_rate}
              onChange={(e) => setSettings({ ...settings, default_rate: parseFloat(e.target.value) || 0 })}
              className="h-12 text-lg font-heading"
              data-testid="default-rate"
            />
            <span className="font-heading font-bold text-2xl text-slate-900 dark:text-white">%</span>
          </div>
        </div>

        {/* Category Rates */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
          <h3 className="font-heading font-bold text-xl text-slate-900 dark:text-white uppercase mb-6">
            Category-Specific Rates
          </h3>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            Set custom commission rates for specific categories. Leave blank to use the default rate.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category) => (
              <div key={category} className="space-y-2">
                <Label className="font-medium text-slate-700 dark:text-slate-300">
                  {category}
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    placeholder={`${settings.default_rate}%`}
                    value={settings.category_rates[category] || ''}
                    onChange={(e) => updateCategoryRate(category, e.target.value)}
                    className="h-10"
                    data-testid={`rate-${category.toLowerCase().replace(' ', '-')}`}
                  />
                  <span className="text-slate-500">%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Info */}
        <div className="mt-8 p-4 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            <strong>Note:</strong> Commission rates are applied to the total sale value. 
            Category-specific rates override the default rate for parts in that category.
          </p>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCommissions;
